import AddIcon from "@mui/icons-material/Add";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import InsertPageBreakOutlinedIcon from "@mui/icons-material/InsertPageBreakOutlined";
import MergeIcon from "@mui/icons-material/Merge";
import PlaylistAddOutlinedIcon from "@mui/icons-material/PlaylistAddOutlined";
import {
    closestCenter,
    DndContext,
    DragEndEvent,
    DragOverlay,
    PointerSensor,
    useSensor,
    useSensors
} from "@dnd-kit/core";
import {arrayMove, SortableContext, verticalListSortingStrategy} from "@dnd-kit/sortable";
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    List,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Popover,
    Stack,
    TextField
} from "@mui/material";
import Typography from "@mui/material/Typography";
import {LocalizedText} from "crqlar-react-common/dist/models/common/localizedText.model";
import {Survey, SurveyFormat, SurveyQuestionType} from "crqlar-react-common/dist/models/survey/survey.model";
import {SurveyQuestion} from "crqlar-react-common/dist/models/survey/surveyQuestion.model";
import React, {useState} from "react";
import {useTranslation} from "react-i18next";
import {FLOATING_SHADOW} from "../../common/newVariablePicker/shared/floatingShadow";
import {useGuestCategories} from "../../guestCategories/useGuestCategories";
import {
    ADDABLE_TYPES,
    canAddType,
    clearConditionsReferencing,
    insertQuestion,
    isScreen,
    questionsDependingOn,
    reorderQuestions
} from "../shared/surveyQuestions.util";
import {surveyMarkdownToPlainText} from "../shared/surveyMarkdownText.util";
import {isScreenType} from "../shared/surveyQuestionTypeFacts";
import {getSurveyQuestionTypeIcon, getSurveyQuestionTypeLabelKey} from "../shared/surveyQuestionTypes.util";
import {describeVisibilityRule} from "../shared/surveyVisibilityRules.util";
import {PanelField} from "./SurveyPanelSection";
import SurveyQuestionRow, {QuestionRowData, SurveyQuestionRowBase} from "./SurveyQuestionRow";
import SurveySectionDivider, {SectionDividerData, SurveySectionDividerBase} from "./SurveySectionDivider";
import SurveySidebarPanel from "./SurveySidebarPanel";
import {
    createEditorQuestion,
    createEditorScreen,
    duplicateEditorQuestion,
    EditorListItem,
    fromEditorListItems,
    toEditorListItems,
    withSwitchedOff
} from "./surveyEditorQuestions.util";

const ANSWERABLE_TYPES = ADDABLE_TYPES.filter(type => !isScreenType(type));

const menuListSx = {
    px: 0.5,
    '& .MuiMenuItem-root': {
        borderRadius: 1,
        '&.Mui-selected': {
            bgcolor: 'action.selected',
            '&:hover': {bgcolor: 'action.hover'},
        },
    },
} as const;

// Outlined with one dim shadow, like the editor's other floating surfaces; elevation 0 replaces the Popover's 8.
const menuPaperProps = {variant: 'outlined', elevation: 0, sx: {boxShadow: FLOATING_SHADOW}} as const;

/** One row of the list: a question, or in One section per page a divider above a section. */
type ListEntry =
    | {kind: 'question', id: string, row: QuestionRowData}
    | {kind: 'break', id: string, fixed: boolean, divider: SectionDividerData};

const isFixedEntry = (entry: ListEntry): entry is Extract<ListEntry, {kind: 'break'}> => (
    entry.kind === 'break' && entry.fixed
);

const itemId = (item: EditorListItem) => (item.kind === 'question' ? item.question.id! : item.id);

interface Props {
    survey: Survey
    questions: SurveyQuestion[]
    language: string
    selectedQuestionId?: string
    onSelectQuestion: (questionId: string) => void
    /** A divider click: select the section's first question and focus the section heading in the canvas. */
    onSelectSection: (questionId: string) => void
    onQuestionsChange: (questions: SurveyQuestion[]) => void
    onFormatChange: (format: SurveyFormat) => void
    disabled?: boolean
}

function SurveyQuestionsPanel({
    survey,
    questions,
    language,
    selectedQuestionId,
    onSelectQuestion,
    onSelectSection,
    onQuestionsChange,
    onFormatChange,
    disabled
}: Props) {
    const {t} = useTranslation();
    const {categories: guestCategories} = useGuestCategories();

    const [addAnchor, setAddAnchor] = useState<HTMLElement | null>(null);
    // The add menu either puts a question at the end, or opens a new last section with it.
    const [addingSection, setAddingSection] = useState(false);
    const [rowMenu, setRowMenu] = useState<{anchor: HTMLElement, questionId: string} | null>(null);
    const [dividerMenu, setDividerMenu] = useState<{anchor: HTMLElement, id: string} | null>(null);
    const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
    const [activeDragId, setActiveDragId] = useState<string | null>(null);

    const sensors = useSensors(useSensor(PointerSensor, {activationConstraint: {distance: 4}}));

    const languages = survey.languages ?? [];
    const previewLanguage = language;
    const sections = survey.format === SurveyFormat.SECTIONS;

    const localizeTitle = (text?: LocalizedText) => {
        if (!text) return '';
        return surveyMarkdownToPlainText(text[previewLanguage] || text['en'] || Object.values(text)[0] || '');
    };

    const toRow = (question: SurveyQuestion, number?: number): QuestionRowData => ({
        question,
        number,
        title: localizeTitle(question.title).trim(),
        missingLanguages: languages.filter(code => !question.title?.[code]?.trim()),
        visibilitySummaries: (question.visibilityRules ?? []).map(
            rule => describeVisibilityRule(rule, questions, previewLanguage, t, guestCategories)
        ),
    });

    // A section title is optional: only one written in some of the survey's languages but not all is missing any.
    const sectionMissingLanguages = (title?: LocalizedText) => {
        const missing = languages.filter(code => !title?.[code]?.trim());
        return missing.length < languages.length ? missing : [];
    };

    const welcome = questions.find(question => question.type === SurveyQuestionType.WELCOME);
    const endScreen = questions.find(question => question.type === SurveyQuestionType.END_SCREEN);
    const answerable = questions.filter(question => !isScreen(question));
    const welcomeRow = welcome ? toRow(welcome) : undefined;
    const endScreenRow = endScreen ? toRow(endScreen) : undefined;

    /*
        One section per page lists a divider above every section, and every change to the list goes through these
        items: the section flags are written back from the list order and never travel with a question. The other
        formats list the questions alone and hand them back as they are, so a stored break moves with its question
        there and shows again after a switch back.
    */
    const items: EditorListItem[] = sections
        ? toEditorListItems(answerable)
        : answerable.map((question): EditorListItem => ({kind: 'question', question}));

    /** The answerable questions the items stand for, with the welcome and end screens put back around them. */
    const questionsFrom = (next: EditorListItem[]) => reorderQuestions([
        ...questions.filter(isScreen),
        ...(sections
            ? fromEditorListItems(next)
            : next.flatMap(item => (item.kind === 'question' ? [item.question] : []))),
    ]);

    // Question numbers run on across sections; dividers count the sections.
    let questionCount = 0;
    let sectionCount = 0;
    const entries = items.map((item, index): ListEntry => {
        if (item.kind === 'question') {
            questionCount += 1;
            return {kind: 'question', id: item.question.id!, row: toRow(item.question, questionCount)};
        }

        sectionCount += 1;
        // toEditorListItems puts a question right below every break.
        const below = items[index + 1];
        return {
            kind: 'break',
            id: item.id,
            fixed: !!item.fixed,
            divider: {
                id: item.id,
                number: sectionCount,
                title: localizeTitle(item.sectionTitle).trim(),
                missingLanguages: sectionMissingLanguages(item.sectionTitle),
                questionId: below?.kind === 'question' ? below.question.id! : '',
            },
        };
    });

    // "Section 1" sits above the sortable rows and never moves.
    const fixedEntry = entries.find(isFixedEntry);
    const sortableEntries = entries.filter(entry => !isFixedEntry(entry));

    const canAddWelcome = canAddType(questions, SurveyQuestionType.WELCOME);
    const canAddEndScreen = canAddType(questions, SurveyQuestionType.END_SCREEN);

    const handleDragEnd = (event: DragEndEvent) => {
        setActiveDragId(null);

        const {active, over} = event;
        if (!over || active.id === over.id) return;

        // The fixed "Section 1" is no sortable item, so it is never moved and stays first.
        const from = items.findIndex(item => itemId(item) === active.id);
        const to = items.findIndex(item => itemId(item) === over.id);
        if (from === -1 || to === -1) return;

        onQuestionsChange(questionsFrom(arrayMove(items, from, to)));
    };

    const addQuestion = (type: SurveyQuestionType) => {
        const question = createEditorQuestion(type);
        setAddAnchor(null);

        if (sections && addingSection) {
            // A section is never empty: it opens with its first question, and the canvas puts the cursor in its title.
            onQuestionsChange(questionsFrom([
                ...items,
                {kind: 'break', id: `break-${question.id}`},
                {kind: 'question', question},
            ]));
            onSelectSection(question.id!);
            return;
        }

        onQuestionsChange(insertQuestion(questions, question));
        onSelectQuestion(question.id!);
    };

    const openAddMenu = (anchor: HTMLElement, section: boolean) => {
        setAddingSection(section);
        setAddAnchor(anchor);
    };

    const addScreen = (type: SurveyQuestionType) => {
        const screen = createEditorScreen(type, languages);
        onQuestionsChange(insertQuestion(questions, screen));
        onSelectQuestion(screen.id!);
        setAddAnchor(null);
    };

    const duplicateQuestion = (questionId: string) => {
        const index = items.findIndex(item => item.kind === 'question' && item.question.id === questionId);
        const original = items[index];
        if (!original || original.kind !== 'question') return;

        // Right below its original, so in the same section; a copy never opens one.
        const copy = duplicateEditorQuestion(original.question);
        const next = [...items];
        next.splice(index + 1, 0, {kind: 'question', question: copy});

        onQuestionsChange(questionsFrom(next));
        onSelectQuestion(copy.id!);
        setRowMenu(null);
    };

    /*
        The Delete dialog's way out. Off keeps the question, its place, its section and the conditions that point at
        it; only publishing leaves it out. The Visibility section switches it on again.
    */
    const switchOffQuestion = (questionId: string) => {
        onQuestionsChange(questions.map(question => (
            question.id === questionId ? withSwitchedOff(question, true) : question
        )));
        setPendingDeleteId(null);
    };

    const deleteQuestion = (questionId: string) => {
        const remaining = items.filter(item => item.kind !== 'question' || item.question.id !== questionId);
        onQuestionsChange(clearConditionsReferencing(questionsFrom(remaining), questionId));
        setPendingDeleteId(null);
    };

    const startSection = (questionId: string) => {
        const index = items.findIndex(item => item.kind === 'question' && item.question.id === questionId);
        if (index === -1) return;

        const next = [...items];
        next.splice(index, 0, {kind: 'break', id: `break-${questionId}`});

        onQuestionsChange(questionsFrom(next));
        setRowMenu(null);
    };

    const mergeSection = (breakId: string) => {
        // The lower section's title goes with its divider.
        onQuestionsChange(questionsFrom(items.filter(item => item.kind !== 'break' || item.id !== breakId)));
        setDividerMenu(null);
    };

    const pendingDelete = questions.find(question => question.id === pendingDeleteId);
    const dependents = pendingDeleteId ? questionsDependingOn(questions, pendingDeleteId) : [];
    const rowMenuQuestion = questions.find(question => question.id === rowMenu?.questionId);
    const rowMenuIsScreen = !!rowMenuQuestion && isScreen(rowMenuQuestion);
    // A new section starts above a question that follows another one, never on a section's first.
    const rowMenuIndex = items.findIndex(item => item.kind === 'question' && item.question.id === rowMenu?.questionId);
    const canStartSection = sections && rowMenuIndex > 0 && items[rowMenuIndex - 1].kind === 'question';

    const addButton = (
        <Stack direction={'row'} justifyContent={'flex-end'} flexWrap={'wrap'} gap={1}>
            {sections && (
                <Button
                    size={'small'}
                    startIcon={<PlaylistAddOutlinedIcon/>}
                    disabled={disabled}
                    onClick={event => openAddMenu(event.currentTarget, true)}
                >
                    {t('Add section')}
                </Button>
            )}
            <Button
                size={'small'}
                startIcon={<AddIcon/>}
                disabled={disabled}
                onClick={event => openAddMenu(event.currentTarget, false)}
            >
                {t('Add question')}
            </Button>
        </Stack>
    );

    return (
        <SurveySidebarPanel>
            <Stack gap={1.5} sx={{pt: 3}}>
                <PanelField label={t('Format')}>
                    <TextField
                        select
                        size={'small'}
                        fullWidth
                        disabled={disabled}
                        value={survey.format ?? SurveyFormat.SINGLE_PAGE}
                        onChange={(event) => onFormatChange(event.target.value as SurveyFormat)}
                        inputProps={{'aria-label': t('Format')}}
                    >
                        <MenuItem value={SurveyFormat.SINGLE_PAGE}>{t('All on one page')}</MenuItem>
                        <MenuItem value={SurveyFormat.MULTI_PAGE}>{t('One question per page')}</MenuItem>
                        <MenuItem value={SurveyFormat.SECTIONS}>{t('One section per page')}</MenuItem>
                    </TextField>
                </PanelField>

                {!questions.length && (
                    <Typography variant={'body2'} color={'text.secondary'}>
                        {t('No questions yet. Add the first one to get started.')}
                    </Typography>
                )}

                <List disablePadding>
                    {!!welcomeRow && (
                        <Box sx={{mb: 0.5}}>
                            <SurveyQuestionRowBase
                                row={welcomeRow}
                                selected={welcomeRow.question.id === selectedQuestionId}
                                onSelect={() => onSelectQuestion(welcomeRow.question.id!)}
                                onOpenMenu={anchor => setRowMenu({anchor, questionId: welcomeRow.question.id!})}
                            />
                        </Box>
                    )}

                    {!!fixedEntry && (
                        <Box sx={{pt: 1, mb: 0.5}}>
                            <SurveySectionDividerBase
                                divider={fixedEntry.divider}
                                onSelect={() => onSelectSection(fixedEntry.divider.questionId)}
                            />
                        </Box>
                    )}

                    <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragStart={event => {
                            const id = String(event.active.id);
                            setActiveDragId(id);
                            // A divider moves a section boundary and selects nothing.
                            if (sortableEntries.some(entry => entry.kind === 'question' && entry.id === id)) {
                                onSelectQuestion(id);
                            }
                        }}
                        onDragCancel={() => setActiveDragId(null)}
                        onDragEnd={handleDragEnd}
                    >
                        <SortableContext
                            items={sortableEntries.map(entry => entry.id)}
                            strategy={verticalListSortingStrategy}
                        >
                            {sortableEntries.map(entry => entry.kind === 'break' ? (
                                <SurveySectionDivider
                                    key={entry.id}
                                    divider={entry.divider}
                                    onSelect={() => onSelectSection(entry.divider.questionId)}
                                    onOpenMenu={anchor => setDividerMenu({anchor, id: entry.id})}
                                />
                            ) : (
                                <SurveyQuestionRow
                                    key={entry.id}
                                    row={entry.row}
                                    selected={entry.id === selectedQuestionId}
                                    onSelect={() => onSelectQuestion(entry.id)}
                                    onOpenMenu={anchor => setRowMenu({anchor, questionId: entry.id})}
                                />
                            ))}
                        </SortableContext>

                        <DragOverlay>
                            {(() => {
                                const activeEntry = sortableEntries.find(entry => entry.id === activeDragId);
                                if (!activeEntry) return null;

                                return activeEntry.kind === 'break' ? (
                                    // The overlay takes the sortable divider's box, space above included.
                                    <Box sx={{pt: 1}}>
                                        <SurveySectionDividerBase
                                            divider={activeEntry.divider}
                                            lifted
                                            handleProps={{}}
                                            onSelect={() => undefined}
                                            onOpenMenu={() => undefined}
                                        />
                                    </Box>
                                ) : (
                                    <SurveyQuestionRowBase
                                        row={activeEntry.row}
                                        selected
                                        lifted
                                        handleProps={{}}
                                        onSelect={() => undefined}
                                        onOpenMenu={() => undefined}
                                    />
                                );
                            })()}
                        </DragOverlay>
                    </DndContext>

                    {!!endScreenRow && (
                        <Box sx={{mb: 0.5}}>
                            <SurveyQuestionRowBase
                                row={endScreenRow}
                                selected={endScreenRow.question.id === selectedQuestionId}
                                onSelect={() => onSelectQuestion(endScreenRow.question.id!)}
                                onOpenMenu={anchor => setRowMenu({anchor, questionId: endScreenRow.question.id!})}
                            />
                        </Box>
                    )}
                </List>

                {addButton}

                {!!questions.length && canAddEndScreen && (
                    <Alert
                        severity={'info'}
                        action={
                            <Button
                                size={'small'}
                                disabled={disabled}
                                onClick={() => addScreen(SurveyQuestionType.END_SCREEN)}
                            >
                                {t('Add end screen')}
                            </Button>
                        }
                    >
                        {t('This survey has no end screen.')}
                    </Alert>
                )}

                <Menu
                    anchorEl={addAnchor}
                    open={!!addAnchor}
                    onClose={() => setAddAnchor(null)}
                    MenuListProps={{sx: menuListSx}}
                    slotProps={{paper: menuPaperProps}}
                >
                    {/* A new section opens with a question, never with a screen. */}
                    {canAddWelcome && !addingSection && (
                        <MenuItem onClick={() => addScreen(SurveyQuestionType.WELCOME)}>
                            <ListItemIcon>
                                {getSurveyQuestionTypeIcon(SurveyQuestionType.WELCOME, 18)}
                            </ListItemIcon>
                            <ListItemText>
                                {t(getSurveyQuestionTypeLabelKey(SurveyQuestionType.WELCOME))}
                            </ListItemText>
                        </MenuItem>
                    )}

                    {canAddWelcome && !addingSection && <Divider sx={{my: 0.5}}/>}

                    {ANSWERABLE_TYPES.map(type => (
                        <MenuItem key={type} onClick={() => addQuestion(type)}>
                            <ListItemIcon>{getSurveyQuestionTypeIcon(type, 18)}</ListItemIcon>
                            <ListItemText>{t(getSurveyQuestionTypeLabelKey(type))}</ListItemText>
                        </MenuItem>
                    ))}

                    {canAddEndScreen && !addingSection && <Divider sx={{my: 0.5}}/>}

                    {canAddEndScreen && !addingSection && (
                        <MenuItem onClick={() => addScreen(SurveyQuestionType.END_SCREEN)}>
                            <ListItemIcon>
                                {getSurveyQuestionTypeIcon(SurveyQuestionType.END_SCREEN, 18)}
                            </ListItemIcon>
                            <ListItemText>
                                {t(getSurveyQuestionTypeLabelKey(SurveyQuestionType.END_SCREEN))}
                            </ListItemText>
                        </MenuItem>
                    )}
                </Menu>

                <Menu
                    anchorEl={rowMenu?.anchor}
                    open={!!rowMenu && !rowMenuIsScreen}
                    onClose={() => setRowMenu(null)}
                    MenuListProps={{sx: menuListSx}}
                    slotProps={{paper: menuPaperProps}}
                >
                    {canStartSection && (
                        <MenuItem disabled={disabled} onClick={() => rowMenu && startSection(rowMenu.questionId)}>
                            <ListItemIcon>
                                <InsertPageBreakOutlinedIcon fontSize={'small'}/>
                            </ListItemIcon>
                            <ListItemText>{t('Start new section here')}</ListItemText>
                        </MenuItem>
                    )}

                    <MenuItem disabled={disabled} onClick={() => rowMenu && duplicateQuestion(rowMenu.questionId)}>
                        <ListItemIcon>
                            <ContentCopyIcon fontSize={'small'}/>
                        </ListItemIcon>
                        <ListItemText>{t('Duplicate')}</ListItemText>
                    </MenuItem>

                    <Divider sx={{my: 0.5}}/>

                    <MenuItem
                        disabled={disabled}
                        sx={{color: 'error.main'}}
                        onClick={() => {
                            setPendingDeleteId(rowMenu?.questionId ?? null);
                            setRowMenu(null);
                        }}
                    >
                        <ListItemIcon sx={{color: 'inherit'}}>
                            <DeleteOutlineIcon fontSize={'small'}/>
                        </ListItemIcon>
                        <ListItemText>{t('Delete')}</ListItemText>
                    </MenuItem>
                </Menu>

                <Menu
                    anchorEl={dividerMenu?.anchor}
                    open={!!dividerMenu}
                    onClose={() => setDividerMenu(null)}
                    MenuListProps={{sx: menuListSx}}
                    slotProps={{paper: menuPaperProps}}
                >
                    <MenuItem disabled={disabled} onClick={() => dividerMenu && mergeSection(dividerMenu.id)}>
                        <ListItemIcon>
                            <MergeIcon fontSize={'small'}/>
                        </ListItemIcon>
                        <ListItemText>{t('Merge with section above')}</ListItemText>
                    </MenuItem>
                </Menu>

                <Popover
                    anchorEl={rowMenu?.anchor}
                    open={!!rowMenu && rowMenuIsScreen}
                    onClose={() => setRowMenu(null)}
                    anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
                    transformOrigin={{vertical: 'top', horizontal: 'right'}}
                    slotProps={{paper: menuPaperProps}}
                >
                    <Typography variant={'body2'} color={'text.secondary'} sx={{p: 1.5, maxWidth: 260}}>
                        {t('The welcome and end screens frame the survey and cannot be deleted.')}
                    </Typography>
                </Popover>

                <Dialog open={!!pendingDelete} onClose={() => setPendingDeleteId(null)} maxWidth={'xs'} fullWidth>
                    <DialogTitle>{t('Delete question')}</DialogTitle>
                    <DialogContent>
                        <Stack gap={2}>
                            {/* Deleted answers stay stored but drop out of view - which is what switching off avoids. */}
                            <Typography variant={'body2'} color={'text.secondary'}>
                                {t('This removes the question from the survey. Answers already collected stay stored, but Evaluation and the submission details no longer show them.')}
                            </Typography>

                            {!pendingDelete?.switchedOff && (
                                <Typography variant={'body2'} color={'text.secondary'}>
                                    {t('To hide it from guests but keep it and its answers in view, switch it off instead.')}
                                </Typography>
                            )}

                            {!!dependents.length && (
                                <Alert severity={'warning'}>
                                    {t(
                                        '{{n}} other question(s) only show based on this answer. Their condition will be removed.',
                                        {n: dependents.length}
                                    )}
                                </Alert>
                            )}
                        </Stack>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setPendingDeleteId(null)}>{t('Cancel')}</Button>
                        {!pendingDelete?.switchedOff && (
                            <Button onClick={() => pendingDeleteId && switchOffQuestion(pendingDeleteId)}>
                                {t('Switch off instead')}
                            </Button>
                        )}
                        <Button
                            color={'error'}
                            variant={'contained'}
                            onClick={() => pendingDeleteId && deleteQuestion(pendingDeleteId)}
                        >
                            {t('Delete')}
                        </Button>
                    </DialogActions>
                </Dialog>
            </Stack>
        </SurveySidebarPanel>
    );
}

export default SurveyQuestionsPanel;
